/**
 * CONN-PROBE-3 — helpers PUROS de apresentação do diagnóstico de conectividade.
 *
 * Consome o contrato tipado do backend (ConnProbeOut do F1/F2):
 *   conn = { lan:  { ok, loss, rtt, age },
 *            inet: { ok, loss, rtt, age },
 *            dns:  { ok, ms, age } }  |  null
 *
 * Observabilidade APENAS — nada aqui aciona comando/relé/reboot/alerta/watchdog.
 *
 * Janelas de "desatualizado" (stale) conservadoras, coerentes com o firmware:
 *   - LAN/INET medidos ~a cada 60 s  → stale após 180 s
 *   - DNS medido       ~a cada 300 s → stale após 600 s
 * `age` vem em segundos desde a última medição (int). `rtt`/`ms` = -1 é
 * SENTINELA de "sem resposta" (ping/resolução falhou), nunca "-1 ms".
 */

export const CONN_STALE_S = { lan: 180, inet: 180, dns: 600 };

// Estados por alvo: "ok" | "fail" | "stale" | "nodata"
// Estado do card: + "unsupported" (conn_supported=false / conn=null)
export const CONN_STATE = {
  OK: "ok",
  FAIL: "fail",
  STALE: "stale",
  NODATA: "nodata",
  UNSUPPORTED: "unsupported",
};

function _num(v) {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

/** age (segundos) → "agora" | "há 12 s" | "há 3 min" | "há 2 h". null/absurdo → "—". */
export function formatConnAge(age) {
  const s = _num(age);
  if (s === null || s < 0 || s > 1e9) return "—";
  if (s < 10) return "agora";
  if (s < 60) return `há ${Math.floor(s)} s`;
  if (s < 3600) return `há ${Math.floor(s / 60)} min`;
  return `há ${Math.floor(s / 3600)} h`;
}

/** rtt/ms → "2 ms" | "Sem resposta" (sentinela -1) | "—" (ausente). */
export function formatRtt(rtt) {
  const n = _num(rtt);
  if (n === null) return "—";
  if (n === -1) return "Sem resposta";
  if (n < 0 || n > 60000) return "—"; // absurdo não quebra o componente
  return `${n} ms`;
}

/** loss → "0% perda" | "100% perda" | "—". */
export function formatLoss(loss) {
  const n = _num(loss);
  if (n === null || n < 0 || n > 100) return "—";
  return `${n}% perda`;
}

function _targetState(t, staleS) {
  if (!t || typeof t !== "object") return CONN_STATE.NODATA;
  const ok = typeof t.ok === "boolean" ? t.ok : null;
  const age = _num(t.age);
  if (ok === null) return CONN_STATE.NODATA;
  // falha explícita (ok=false) ou sentinela de sem-resposta manda em cima de stale
  if (ok === false || _num(t.rtt) === -1 || _num(t.ms) === -1) return CONN_STATE.FAIL;
  if (age !== null && age > staleS) return CONN_STATE.STALE;
  return CONN_STATE.OK;
}

const _STATE_LABEL = {
  ok: "OK",
  fail: "Falha",
  stale: "Dados antigos",
  nodata: "Sem dados",
  unsupported: "Não suportado",
};

export function stateLabel(state) {
  return _STATE_LABEL[state] || "—";
}

/** Classe de texto (cor) por estado — usada nos indicadores compactos L·I·D. */
export function stateTextClass(state) {
  switch (state) {
    case CONN_STATE.OK:
      return "text-noc-ok";
    case CONN_STATE.FAIL:
      return "text-noc-err";
    case CONN_STATE.STALE:
      return "text-noc-warn";
    default:
      return "text-noc-faint";
  }
}

/** Classe de badge do projeto por estado (não depende só de cor: label textual). */
export function stateBadgeClass(state) {
  switch (state) {
    case CONN_STATE.OK:
      return "badge-ok";
    case CONN_STATE.FAIL:
      return "badge-err";
    case CONN_STATE.STALE:
      return "badge-warn";
    default:
      return "badge-muted";
  }
}

/**
 * Monta a "view" pronta para render dos 3 alvos + estado geral.
 * @param conn bloco tipado (ou null)
 * @param connSupported opcional; quando ausente, deriva de conn ter ≥1 alvo.
 */
export function connProbeView(conn, connSupported) {
  const c = conn && typeof conn === "object" ? conn : null;
  const supported =
    connSupported === true ||
    (connSupported === undefined &&
      !!c &&
      (c.lan != null || c.inet != null || c.dns != null));

  if (!supported) {
    return { supported: false, overall: CONN_STATE.UNSUPPORTED, lan: null, inet: null, dns: null };
  }

  const mk = (t, kind, staleS) => {
    const state = _targetState(t, staleS);
    const view = { state, label: stateLabel(state), badge: stateBadgeClass(state), age: formatConnAge(t && t.age) };
    if (kind === "dns") {
      view.rtt = formatRtt(t && t.ms);
    } else {
      view.rtt = formatRtt(t && t.rtt);
      view.loss = formatLoss(t && t.loss);
    }
    return view;
  };

  const lan = mk(c ? c.lan : null, "target", CONN_STALE_S.lan);
  const inet = mk(c ? c.inet : null, "target", CONN_STALE_S.inet);
  const dns = mk(c ? c.dns : null, "dns", CONN_STALE_S.dns);

  // Estado geral p/ chip/marker: prioridade Falha > Dados antigos > OK > Sem dados.
  const states = [lan.state, inet.state, dns.state];
  let overall = CONN_STATE.NODATA;
  if (states.includes(CONN_STATE.FAIL)) overall = CONN_STATE.FAIL;
  else if (states.includes(CONN_STATE.STALE)) overall = CONN_STATE.STALE;
  else if (states.includes(CONN_STATE.OK)) overall = CONN_STATE.OK;

  return { supported: true, overall, lan, inet, dns };
}

/**
 * Resumo textual compacto p/ tooltip do marker do mapa (HTML-string, sem React).
 * @returns { text, cls, supported }  cls ∈ "err" | "warn" | null (classe do tooltip).
 */
export function connSummary(conn, connSupported) {
  const v = connProbeView(conn, connSupported);
  if (!v.supported) {
    return { text: "Diagnóstico de conectividade indisponível", cls: null, supported: false };
  }
  const cls =
    v.overall === CONN_STATE.FAIL ? "err" : v.overall === CONN_STATE.STALE ? "warn" : null;
  return {
    text: `Conexão: LAN ${v.lan.label} · INET ${v.inet.label} · DNS ${v.dns.label}`,
    cls,
    supported: true,
  };
}

/** Resumo compacto p/ chip da lista / marker do mapa. */
export function connChip(conn, connSupported) {
  const v = connProbeView(conn, connSupported);
  if (!v.supported) return { state: CONN_STATE.UNSUPPORTED, label: "—", badge: "badge-muted", title: "Sem diagnóstico de conectividade" };
  const map = {
    ok: "OK",
    fail: "Internet/rede falhou",
    stale: "Dados antigos",
    nodata: "Sem dados",
  };
  return {
    state: v.overall,
    label: map[v.overall] || "—",
    badge: stateBadgeClass(v.overall),
    title: `Rede local: ${v.lan.label} · Internet: ${v.inet.label} · DNS: ${v.dns.label}`,
  };
}
